import React, { useEffect, useRef } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { LayoutDashboard, ShoppingBag, Package, Boxes, Users, FileText, ShieldCheck, LogOut, ArrowLeft } from "lucide-react";
import { Toaster } from "../../components/ui/sonner";
import { toast } from "sonner";
import api from "../../lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/roles", label: "Roles", icon: ShieldCheck, anyPermission: ["admin.manage_roles", "roles.create"] },
  { to: "/admin/staff", label: "Staff", icon: Users, anyPermission: ["staff.manage", "staff.view", "staff.create"] },
];

const POLL_INTERVAL_MS = 30000;

export default function AdminLayout() {
  const { user, checking, logout, can, isStaff } = useAuth();
  const navigate = useNavigate();
  const seenIdsRef = useRef(null); // null until the first poll completes — prevents a toast storm on initial load

  const canViewOrders = !!user && can("orders.view");

  // New-order notification: polls unclaimed orders every 30s (not
  // tracking_status=="pending" — prepaid orders jump to "processing" within
  // seconds of checkout, so that status barely exists in practice). Only
  // toasts for orders that are actually NEW since the last poll, not the
  // raw count every time. Lives here rather than on the Orders page itself
  // so it works from any admin page.
  useEffect(() => {
    if (!canViewOrders) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/admin/orders?unclaimed=true");
        if (cancelled) return;
        const ids = new Set(data.map((o) => o.id));

        if (seenIdsRef.current) {
          const newOnes = data.filter((o) => !seenIdsRef.current.has(o.id));
          if (newOnes.length > 0) {
            toast.success(
              newOnes.length === 1
                ? `New order received — ${newOnes[0].customer?.name || "Guest"}`
                : `${newOnes.length} new orders received`
            );
          }
        }
        seenIdsRef.current = ids;
      } catch (e) {
        // Silent — a background poll shouldn't interrupt whatever the person is doing
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canViewOrders]);

  if (checking) return <div className="p-12">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (!isStaff) return <Navigate to="/" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;

  return (
    <div className="min-h-screen bg-welljio-bg flex" data-testid="admin-layout">
      <aside className="w-64 bg-white border-r border-slate-100 px-5 py-8 hidden lg:flex flex-col sticky top-0 self-start" style={{ minHeight: "100vh" }}>
        <div className="px-2 mb-8">
          <p className="text-xs uppercase tracking-widest text-welljio-subtle">Welljio</p>
          <p className="font-heading text-xl font-semibold">{user.role_name || "Staff"} Console</p>
        </div>
        <nav className="space-y-1 flex-1">
          {NAV.filter((n) => !n.permission && !n.anyPermission || (n.permission && can(n.permission)) || (n.anyPermission && n.anyPermission.some(can))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm transition-colors ${
                  isActive ? "bg-welljio-ink text-white" : "text-welljio-muted hover:bg-welljio-tint"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-4 mt-4 space-y-1">
          <button
            onClick={() => navigate("/")}
            data-testid="admin-exit-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm text-welljio-muted hover:bg-welljio-tint"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back to store
          </button>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm text-welljio-muted hover:bg-welljio-tint"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5}/> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-6 lg:px-10 py-10">
        <Outlet />
      </main>
      <Toaster position="bottom-right" />
    </div>
  );
}
